<script lang="ts">
    import { ref, onMounted, inject } from 'vue';
    import vm from '../Journeys.viewmodel';
    import { JourneyStatus } from '@journeys/Enums';
    import { api } from '@cwd/lib/main';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import List from '@shared/components/List/List.vue';
    import JourneyListItem from '../components/JourneyListItem.vue';
    import { type Journey } from '@journeys/journeys';
    import { journeysOrderOptions } from './JourneyStart.vue';
    import { CrossSVG } from '@cwd/shared/assets/vector/ui/IconsUI';
    import { useRouter } from 'vue-router';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { UserEventService } from '@shared/Root/UserEvent.service';

    export default {
        name: 'Page: Journey New',
    };
</script>

<script lang="ts" setup>
    const { editor, account } = vm;
    const router = useRouter();

    // Get the UserEventService from the injector
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const userEventService = injectorAccess.injector.get(UserEventService);

    onMounted(() => {
        getFiltered('Template');
    });

    const journeysTabs = {
        all: { name: 'all', display: $Resources.JourneysAllTabLabel },
        drafts: { name: 'drafts', display: $Resources.JourneysDraftsTabLabel },
        active: { name: 'active', display: $Resources.JourneysActiveTabLabel },
        ended: { name: 'ended', display: $Resources.JourneysEndedTabLabel },
    };

    const currentTab = ref('templates');

    const switchTab = (tab: string) => {
        if (tab === 'templates') getFiltered('Template');
        if (tab === 'copyprevious') getFiltered('all');

        currentTab.value = tab;
        currentFilter.value = 'all';
    };

    const currentFilter = ref('all');

    const switchFilter = (filter: string) => {
        if (currentTab.value === 'copyprevious') getFiltered(filter);

        currentFilter.value = filter;
    };

    const list = ref<Journey[]>([]);

    const templatesList = () => {
        if (currentTab.value === 'templates') {
            return filteredList();
        } else {
            return [];
        }
    };

    const previousList = () => {
        if (currentTab.value !== 'templates') {
            return filteredList();
        } else {
            return [];
        }
    };

    const getFiltered = async (filter: string) => {
        let res = await account.listings.get(`/journeys`);

        if (filter === 'draft') {
            res = res.reduce((acc: any, item: any) => (item.Status === JourneyStatus.Draft && acc.push(item), acc), []);
        } else if (filter === 'active') {
            res = res.reduce((acc: any, item: any) => (item.Status === JourneyStatus.Active && acc.push(item), acc), []);
        } else if (filter === 'ended') {
            res = res.reduce((acc: any, item: any) => (item.Status === JourneyStatus.Ended && acc.push(item), acc), []);
        }

        list.value = res as Journey[];
    };

    const filteredList = () => {
        switch (currentTab.value) {
            case 'templates':
                list.value = account.listings.get(`/journeys/Templates`);
                return list.value as Journey[];
            case 'freetemplates':
                return [];
            case 'copyprevious':
                return list.value as Journey[];
            default:
                return [];
        }
    };

    const openJourney = async (journeyId: string) => {
        const clonedJourney = await api.post('/Journeys/Clone/' + journeyId).then((res: any) => res);
        userEventService.registerUserEvent('Journey', 'Created journey', null);
        router.push('/journeys/' + clonedJourney.JourneyId);
    };

    const close = () => {
        router.push('/journeys/drafts');
    };
</script>

<template>
    <header role="banner" class="p-lg-r p-lg-l">
        <div class="d-flex justify-content-between align-items-baseline">
            <h1>{{ $Resources.JourneysTemplatesHeading }}</h1>
            <a href="javascript:void(0)" @click.prevent="close" class="btn btn-subtle">
                <CrossSVG class="toolbar-icon m-xxs-r"></CrossSVG>
                {{ $Resources.CloseBtn }}
            </a>
        </div>
        <p class="m-lg-b">
            {{ $Resources.JourneysTemplatesIntroduction }}
        </p>
    </header>

    <tabs navClass="tabs list-unstyled m-lg-r m-lg-l" :queryParam="false">
        <tab :title="$Resources.JourneysTemplatesTabLabel" @tabClick="() => switchTab('templates')">
            <List
                itemId="JourneyId"
                :items="templatesList()"
                :loading="editor.state.busy"
                :filterKeys="['Title', 'Description']"
                :orderOptions="journeysOrderOptions()"
                displayAs="cards"
                :displayOptions="['default', 'cards']">
                <template #toolbar></template>
                <template #item="{ item, index }">
                    <JourneyListItem
                        :journey="item"
                        :index="index"
                        :tagList="account.listings.getMap('/tags')"
                        :hideStats="true"
                        :hideAbortedIssuesAlert="true"
                        @journeyClicked="openJourney(item.JourneyId)"></JourneyListItem>
                </template>
            </List>
        </tab>
        <tab :title="$Resources.JourneysAsTemplatesTab" @tabClick="() => switchTab('copyprevious')">
            <List
                itemId="JourneyId"
                :items="previousList()"
                :loading="editor.state.busy"
                :filterKeys="['Title', 'Description']"
                :orderOptions="journeysOrderOptions()"
                displayAs="cards"
                :displayOptions="['default', 'cards']"
                :tabs="journeysTabs"
                :currentTab="currentFilter"
                @update:tab="(newVal) => switchFilter(newVal)">
                <template #toolbar></template>
                <template #item="{ item, index }">
                    <JourneyListItem
                        :journey="item"
                        :index="index"
                        :tagList="account.listings.getMap('/tags')"
                        :hideStats="true"
                        :hideAbortedIssuesAlert="true"
                        @journeyClicked="openJourney(item.JourneyId)"></JourneyListItem>
                </template>
            </List>
        </tab>
    </tabs>
</template>
